// Pure model of the navigation chrome rendered by the Vue shell (src/components/nav), which replaced
// the legacy nav / sidebar components. Inputs are plain snapshots taken from the legacy state by
// src/iframe/legacyNavState.js; nothing here touches window, document or the iframe.

// Legacy hasher routes (js/Router.js): 'ecos/...' is the application; '' the blank page a legacy document shows
// until the Vue session opens the application (the portal and login are Vue pages, shown over it).
export function pageFromHash(hash) {
	const path = String(hash ?? '')
		.replace(/^#/, '')
		.replace(/^\/+/, '');
	if (path === '' || path.startsWith('settings')) return 'portal';
	if (path.startsWith('login')) return 'login';
	if (path.startsWith('ecos')) return 'app';
	return 'other';
}

// 'hidden': no chrome at all (login, BO hidden app, delegation frame covering the app with its own).
export function resolveChromeMode({ attached = false, loggedIn = false, page = 'other', hiddenApp = false, delegationFrameOpen = false } = {}) {
	if (!attached || hiddenApp || delegationFrameOpen || !loggedIn) return 'hidden';
	if (page === 'app' || page === 'portal') return page;
	return 'hidden';
}

// The chrome of the current route: the portal route shows the portal bar (the application may keep running
// behind it), a legacy route the application chrome once the legacy shows it, any other route none.
export function chromeModeForRoute(legacyMode, { portal = false, legacy = false } = {}) {
	if (legacyMode === 'hidden') return 'hidden';
	if (portal) return 'portal';
	if (legacy) return legacyMode === 'app' ? 'app' : 'hidden';
	return 'hidden';
}

// Payload labels are the ones hard-coded in the legacy templates (they feed traceView statistics).
const SFA_ENTRIES = {
	vwSFAPromo: { icon: 'view_timeline', label: 'SFA PlanPromo' },
	vwDocObjectives: { icon: 'target', label: 'Menu Objectives' },
	vwWatch: { icon: 'visibility', label: 'menu Watch' },
};
const SFA_ENTRIES_FROM_DOC = ['vwDocObjectives', 'vwWatch'];
const SALES_ENTRIES = {
	vwSalesCatalog: { icon: 'list_alt', label: 'Catalog' },
	vwSalesCatalogReturn: { icon: 'quick_reorder', label: 'CatalogReturn' },
	vwOrdersTracking: { icon: 'list', label: 'Order tracking' },
	vwOrdersTrackingAssort: { icon: 'barcode', label: 'Order tracking Assort' },
};
const DOC_ENTRIES = {
	vwDocFolders: { icon: 'description', label: 'Docs' },
	vwVeille: { icon: 'eye_tracking', label: 'Menu Veille' },
};

// Sub-menus of SALES backed by the hidden vwSalesEdito component (draft carts, offline orders).
export const SALES_SHORTCUTS = [
	{ key: 'draft', labelKey: 'DraftCartMenu', icon: 'draft_orders' },
	{ key: 'offlineErrors', labelKey: 'OfflineOrdersErrors', icon: 'production_quantity_limits' },
	{ key: 'offline', labelKey: 'OfflineOrders', icon: 'sync_problem' },
];

// Views highlighted through another menu entry (sidebar.js evtChangeView).
const ACTIVE_VIEW_ALIASES = {
	vwWidgets: 'widgets',
	vwOrdersValidation: 'vwOrdersTracking',
	vwOrdersStatement: 'vwOrdersTracking',
};

export const activeMenuView = (view) => (view ? (ACTIVE_VIEW_ALIASES[view] ?? view) : null);

const primaryItem = (menu, icon) => ({ key: menu.view, view: menu.view, labelKey: menu.label, icon, payload: { appMenu: menu.appMenu, view: menu.view, label: menu.label } });

// The group comes from the legacy array the item was read from (itemsSfa / itemsSales / itemsDoc),
// never from item.appMenu: sidebar.js fills appMenu with the DB `Target` column.
const groupItems = (sources, entries, appMenu) => {
	const seen = new Set();
	const items = [];
	sources.forEach(({ list, views }) =>
		(list ?? []).forEach((menu) => {
			const entry = entries[menu?.view];
			if (!entry || (views && !views.includes(menu.view)) || seen.has(menu.view)) return;
			seen.add(menu.view);
			items.push({ key: menu.view, view: menu.view, labelKey: menu.label, icon: entry.icon, payload: { appMenu, view: menu.view, label: entry.label } });
		})
	);
	return items;
};

/**
 * @param {object} snapshot
 * @param {boolean} snapshot.onApp          the legacy is on its application page (#ecos)
 * @param {object}  snapshot.menus          { home, agenda, myPos, sfa, sales, doc } menuVM snapshots
 * @param {object}  snapshot.flags          { delegation, tokenSales, tokenDoc, hasAccessToSales, assortOrderActivated }
 * @param {object}  snapshot.shortcuts      { draft, offline, offlineErrors } → { visible, count }
 * @returns {{ primary: object[], groups: { key: string, titleKey: string, items: object[] }[] }}
 */
export function buildLegacyMenu({ onApp = false, menus = {}, flags = {}, shortcuts = {} } = {}) {
	if (!onApp) return { primary: [], groups: [] };

	const delegation = Boolean(flags.delegation);
	const primary = [];
	if (menus.home) primary.push(primaryItem(menus.home, 'home'));
	if (menus.agenda) primary.push(primaryItem(menus.agenda, 'calendar_month'));
	if (menus.myPos && !delegation) primary.push(primaryItem(menus.myPos, 'store'));

	const groups = [];

	// TFS#7451: a delegated session only keeps Home, Agenda and SALES.
	if (!delegation) {
		const sfa = groupItems(
			[
				{ list: menus.sfa, views: null },
				{ list: menus.doc, views: SFA_ENTRIES_FROM_DOC },
			],
			SFA_ENTRIES,
			'menuSFA'
		);
		if (sfa.length) groups.push({ key: 'sfa', titleKey: 'menu_SFA', items: sfa });
	}

	const salesAllowed = Boolean(flags.tokenSales) && (Boolean(flags.hasAccessToSales) || Boolean(flags.assortOrderActivated));
	if (salesAllowed && (menus.sales ?? []).length > 0) {
		const items = groupItems([{ list: menus.sales, views: null }], SALES_ENTRIES, 'menuSALES');
		SALES_SHORTCUTS.forEach((shortcut) => {
			const state = shortcuts[shortcut.key];
			if (state?.visible) items.push({ ...shortcut, shortcut: shortcut.key, count: state.count ?? 0 });
		});
		if (items.length) groups.push({ key: 'sales', titleKey: 'menu_SALES', items });
	}

	if (!delegation && flags.tokenDoc) {
		const doc = groupItems([{ list: menus.doc, views: null }], DOC_ENTRIES, 'menuDOC');
		if (doc.length) groups.push({ key: 'doc', titleKey: 'menu_DOC', items: doc });
	}

	return { primary, groups };
}

// Merchandising products (process 5) have no quantity (cartPreview.js printQuantity).
export const cartQuantityText = (product) => (product?.process === 5 ? 'N/A' : String(product?.quantity ?? 0));

const assortKey = (product) => `${product.apCompanyId}.${product.apDlId}`;

/**
 * Same buckets as cartPreview.js init(): plain lines, RSW lines (sorted by rswId) and assortment
 * lines grouped by company then delivery location (sorted on "<companyId>.<dlId>" as strings).
 */
export function groupCartProducts(products = []) {
	const list = Array.isArray(products) ? products : [];
	const regular = list.filter((p) => !p.rswId && !p.apCompanyId);
	const rsw = list.filter((p) => p.rswId > 0).sort((a, b) => a.rswId - b.rswId);
	const assortLines = list
		.filter((p) => p.apCompanyId > 0)
		.map((product, index) => ({ product, index, key: assortKey(product) }))
		.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : a.index - b.index));

	const assort = [];
	assortLines.forEach(({ product, key }) => {
		let company = assort[assort.length - 1];
		if (!company || company.companyId !== product.apCompanyId) {
			company = { companyId: product.apCompanyId, companyName: product.companyName ?? '', locations: [] };
			assort.push(company);
		}
		let location = company.locations[company.locations.length - 1];
		if (!location || location.key !== key) {
			location = { key, companyId: product.apCompanyId, dlId: product.apDlId, items: [] };
			company.locations.push(location);
		}
		location.items.push(product);
	});

	return { regular, rsw, assort, count: regular.length + rsw.length + assortLines.length };
}

/**
 * Franco (free-shipping threshold) of an assortment delivery location, as cartPreview.js getFranco()
 * renders it. `franco` is salesHelper.companies.calcFranco() output.
 */
export function francoSummary({ company, franco, currency = '', isReturn = false } = {}) {
	if (!company || !franco || isReturn) return null;

	const reached = !(franco.total < company.franco);
	const level = reached ? 'ok' : company.francoMandatory ? 'error' : 'warning';
	const total = company.francoWithDiscount ? franco.totalDiscounted || franco.total : franco.total;
	const devise = company.devise ?? '';
	const value = franco.totalDiscounted || franco.totalPrice;

	return {
		level,
		text: `${Number(total ?? 0).toFixed(2)}${devise} / ${company.franco}${devise}`,
		valueText: value && value !== total ? `${Number(value).toFixed(2)}${currency}` : null,
	};
}
